import { type ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined'
import PersonOutline from '@mui/icons-material/PersonOutline'
import DirectionsCarOutlined from '@mui/icons-material/DirectionsCarOutlined'
import RvHookupOutlined from '@mui/icons-material/RvHookupOutlined'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid2,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import type {
  DashboardMetrics,
  RecentDashboardOrder,
  RecentDashboardTrip,
} from 'store/dashboard/dashboard.types'
import { ACCENT_BLUE } from 'utils/brand-colors'
import { alpha } from '@mui/material/styles'
import FleetRevenueChart from './fleet-revenue-chart'
import FleetOrderDonut from './fleet-order-donut'
import FleetEquipmentPanel from './fleet-equipment-panel'
type Props = {
  loading: boolean
  metrics?: DashboardMetrics
  recentOrders: RecentDashboardOrder[]
  recentTrips: RecentDashboardTrip[]
  recentOrdersLoading: boolean
  recentTripsLoading: boolean
}

const C = {
  blue: ACCENT_BLUE,
  orange: '#D97706',
  red: '#C81E1D',
  green: '#22C55E',
  purple: '#8B5CF6',
  border: '#E5E7EB',
  greyLabel: '#6B7280',
}

const CARD_SHADOW = '0 4px 14px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)'

type KpiCardSpec = {
  title: string
  value: string
  footer: string
  footerColor: string
  icon: ReactNode
  tint: string
  bg: string
  accent: string
}

function KpiIconCard({ title, value, footer, footerColor, icon, tint, bg, accent, cardSx }: KpiCardSpec & { cardSx?: SxProps<Theme> }) {
  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${alpha(C.border, 0.85)}`,
        borderLeft: `4px solid ${accent}`,
        borderRadius: 2,
        bgcolor: '#fff',
        boxShadow: CARD_SHADOW,
        height: '100%',
        ...cardSx,
      }}
    >
      <CardContent
        sx={{
          py: 2,
          px: 2.5,
          height: '100%',
          minHeight: 112,
          display: 'flex',
          alignItems: 'center',
          '&:last-child': { pb: 2 },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ width: '100%' }}
        >
          <Box sx={{ minWidth: 0, pl: 0.5 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#0f172a', lineHeight: 1.2, mb: 0.75 }}>
              {title}
            </Typography>
            <Typography
              sx={{
                fontSize: 30,
                fontWeight: 800,
                color: '#0F172A',
                lineHeight: 1,
                letterSpacing: '-0.03em',
              }}
            >
              {value}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: footerColor, lineHeight: 1.3, mt: 0.75 }}>
              {footer}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: bg,
              color: tint,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              mr: 0.5,
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

const pct = (part: number, total: number) => (total > 0 ? Math.round((part / total) * 100) : 0)

const statusForUtilization = (utilPct: number) =>
  utilPct >= 75
    ? { footer: 'Good', color: C.green }
    : { footer: 'Needs attention', color: C.red }

const ratioValue = (left: number, right: number, loading: boolean) => {
  if (loading) return '—'
  return `${left}/${left + right}`
}

function SummaryPanelCard({
  title,
  children,
  centerContent = false,
  headerAction,
  cardSx,
  contentSx,
}: {
  title: string
  children: ReactNode
  centerContent?: boolean
  headerAction?: ReactNode
  cardSx?: SxProps<Theme>
  contentSx?: SxProps<Theme>
}) {
  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${alpha(C.border, 0.85)}`,
        borderRadius: 2,
        bgcolor: '#fff',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: CARD_SHADOW,
        overflow: 'hidden',
        ...cardSx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pt: 2,
          pb: 1,
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 17, color: '#0f172a' }}>{title}</Typography>
        {headerAction}
      </Box>
      <Box
        sx={{
          px: 2,
          pb: 1.5,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          ...(centerContent && {
            alignItems: 'center',
            justifyContent: 'center',
          }),
          ...contentSx,
        }}
      >
        {children}
      </Box>
    </Card>
  )
}

const FleetDashboardMetrics = ({
  loading,
  metrics,
  recentOrders,
  recentTrips,
  recentOrdersLoading,
  recentTripsLoading,
}: Props) => {
  const t = metrics?.today

  const assignedOrders = t?.orders.assignedWithTrip ?? 0
  const unassignedOrders = t?.orders.activeWithoutTrip ?? 0
  const assignedDrivers = t?.drivers.assignedToday ?? 0
  const availableDrivers = t?.drivers.available ?? 0
  const assignedTractors = t?.tractors.assignedToday ?? 0
  const availableTractors = t?.tractors.available ?? 0
  const assignedTrailers = t?.trailers.assignedToday ?? 0
  const availableTrailers = t?.trailers.available ?? 0

  const orderTotal = assignedOrders + unassignedOrders
  const driverTotal = assignedDrivers + availableDrivers
  const tractorTotal = assignedTractors + availableTractors
  const trailerTotal = assignedTrailers + availableTrailers

  const orderAssignedPct = pct(assignedOrders, orderTotal)
  const driverAvailPct = pct(availableDrivers, driverTotal)
  const tractorUtilPct = pct(assignedTractors, tractorTotal)
  const trailerUtilPct = pct(assignedTrailers, trailerTotal)

  const orderStatus = statusForUtilization(orderAssignedPct)
  const driverUtilPct = pct(assignedDrivers, driverTotal)
  const tractorStatus = statusForUtilization(tractorUtilPct)
  const trailerStatus = statusForUtilization(trailerUtilPct)

  const kpiCards: KpiCardSpec[] = [
    {
      title: 'Orders',
      value: ratioValue(assignedOrders, unassignedOrders, loading),
      footer: loading ? '—' : `${orderAssignedPct}% assigned · ${orderStatus.footer}`,
      footerColor: loading ? C.greyLabel : orderStatus.color,
      icon: <LocalShippingOutlined sx={{ fontSize: 22 }} />,
      tint: C.green,
      bg: alpha(C.green, 0.12),
      accent: C.green,
    },
    {
      title: 'Drivers',
      value: ratioValue(assignedDrivers, availableDrivers, loading),
      footer: loading ? '—' : `${driverAvailPct}% available`,
      footerColor: loading ? C.greyLabel : driverUtilPct >= 75 ? C.green : C.red,
      icon: <PersonOutline sx={{ fontSize: 22 }} />,
      tint: C.blue,
      bg: alpha(C.blue, 0.12),
      accent: C.blue,
    },
    {
      title: 'Tractors',
      value: loading ? '—' : `${tractorUtilPct}%`,
      footer: loading ? '—' : tractorStatus.footer,
      footerColor: loading ? C.greyLabel : tractorStatus.color,
      icon: <DirectionsCarOutlined sx={{ fontSize: 22 }} />,
      tint: C.orange,
      bg: alpha(C.orange, 0.12),
      accent: C.orange,
    },
    {
      title: 'Trailers',
      value: loading ? '—' : `${trailerUtilPct}%`,
      footer: loading ? '—' : trailerStatus.footer,
      footerColor: loading ? C.greyLabel : trailerStatus.color,
      icon: <RvHookupOutlined sx={{ fontSize: 22 }} />,
      tint: C.purple,
      bg: alpha(C.purple, 0.12),
      accent: C.purple,
    },
  ]

  return (
    <Stack spacing={2.5} sx={{ px: 2 }}>
      <Grid2 container spacing={2} alignItems="stretch">
        {kpiCards.map((card) => (
          <Grid2 size={{ xs: 12, sm: 6, lg: 3 }} key={card.title} sx={{ display: 'flex' }}>
            <KpiIconCard {...card} cardSx={{ flex: 1 }} />
          </Grid2>
        ))}
      </Grid2>

      <Grid2 container spacing={2} alignItems="stretch">
        <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: 'flex', minWidth: 0 }}>
          <SummaryPanelCard title="Revenue" cardSx={{ flex: 1, minWidth: 0 }}>
            <FleetRevenueChart metrics={metrics} loading={loading} />
          </SummaryPanelCard>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: 'flex', minWidth: 0 }}>
          <Grid2 container spacing={2} sx={{ flex: 1, width: '100%', minWidth: 0 }} alignItems="stretch">
            <Grid2 size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', minWidth: 0 }}>
              <SummaryPanelCard
                title="Invoice"
                centerContent
                cardSx={{ flex: 1, minWidth: 0 }}
                contentSx={{ overflow: 'auto' }}
              >
                <FleetOrderDonut metrics={metrics} loading={loading} />
              </SummaryPanelCard>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', minWidth: 0 }}>
              <SummaryPanelCard
                title="Equipment"
                cardSx={{ flex: 1, minWidth: 0 }}
                headerAction={
                  <Button
                    component={RouterLink}
                    to="/equipment"
                    size="small"
                    sx={{ textTransform: 'none', fontWeight: 700, color: ACCENT_BLUE, fontSize: 13 }}
                  >
                    View all
                  </Button>
                }
              >
                <FleetEquipmentPanel />
              </SummaryPanelCard>
            </Grid2>
          </Grid2>
        </Grid2>
      </Grid2>

      <Grid2 container spacing={2} alignItems="stretch">
            <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
              <Card
                elevation={0}
                sx={{
                  border: `1px solid ${alpha(C.border, 0.85)}`,
                  borderRadius: 2,
                  bgcolor: '#fff',
                  boxShadow: CARD_SHADOW,
                  overflow: 'hidden',
                  flex: 1,
                  minHeight: 260,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    pt: 2,
                    pb: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 17, color: '#0f172a' }}>Recent orders</Typography>
                  <Button
                    component={RouterLink}
                    to="/order"
                    size="small"
                    sx={{ textTransform: 'none', fontWeight: 700, color: ACCENT_BLUE }}
                  >
                    View all
                  </Button>
                </Box>

                <TableContainer sx={{ flex: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha('#0f172a', 0.03) }}>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            fontSize: 12,
                            color: C.greyLabel,
                            borderBottom: `1px solid ${C.border}`,
                          }}
                        >
                          Order #
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 700,
                            fontSize: 12,
                            color: C.greyLabel,
                            borderBottom: `1px solid ${C.border}`,
                          }}
                        >
                          Created
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentOrdersLoading ? (
                        <TableRow>
                          <TableCell colSpan={2} sx={{ py: 4, color: C.greyLabel }}>
                            Loading…
                          </TableCell>
                        </TableRow>
                      ) : recentOrders.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2} sx={{ py: 4, color: C.greyLabel }}>
                            No recent orders.
                          </TableCell>
                        </TableRow>
                      ) : (
                        recentOrders.map((row, idx) => (
                          <TableRow
                            key={`${row.userOrderId}-${row.createdDate}-${idx}`}
                            hover
                            sx={{ '&:last-child td': { borderBottom: 0 } }}
                          >
                            <TableCell
                              sx={{
                                borderBottom: `1px solid ${C.border}`,
                                fontSize: 14,
                                fontWeight: 600,
                                color: '#111827',
                              }}
                            >
                              {row.userOrderId}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                borderBottom: `1px solid ${C.border}`,
                                fontSize: 13,
                                color: C.greyLabel,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.relativeTime} ago
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, px: 2 }}>
                  <Button
                    component={RouterLink}
                    to="/order"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                  >
                    View all orders
                  </Button>
                </Box>
              </Card>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
              <Card
                elevation={0}
                sx={{
                  border: `1px solid ${alpha(C.border, 0.85)}`,
                  borderRadius: 2,
                  bgcolor: '#fff',
                  boxShadow: CARD_SHADOW,
                  overflow: 'hidden',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    pt: 2,
                    pb: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 17, color: '#0f172a' }}>Recent trips</Typography>
                  <Button
                    component={RouterLink}
                    to="/trip"
                    size="small"
                    sx={{ textTransform: 'none', fontWeight: 700, color: ACCENT_BLUE }}
                  >
                    View all
                  </Button>
                </Box>

                <TableContainer sx={{ flex: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha('#0f172a', 0.03) }}>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            fontSize: 12,
                            color: C.greyLabel,
                            borderBottom: `1px solid ${C.border}`,
                          }}
                        >
                          Trip #
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            fontSize: 12,
                            color: C.greyLabel,
                            borderBottom: `1px solid ${C.border}`,
                          }}
                        >
                          Order #
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            fontSize: 12,
                            color: C.greyLabel,
                            borderBottom: `1px solid ${C.border}`,
                          }}
                        >
                          Driver
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 700,
                            fontSize: 12,
                            color: C.greyLabel,
                            borderBottom: `1px solid ${C.border}`,
                          }}
                        >
                          Created
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentTripsLoading ? (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ py: 4, color: C.greyLabel }}>
                            Loading…
                          </TableCell>
                        </TableRow>
                      ) : recentTrips.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ py: 4, color: C.greyLabel }}>
                            No recent trips.
                          </TableCell>
                        </TableRow>
                      ) : (
                        recentTrips.map((row, idx) => (
                          <TableRow
                            key={`${row.userTripId}-${row.createdDate}-${idx}`}
                            hover
                            sx={{ '&:last-child td': { borderBottom: 0 } }}
                          >
                            <TableCell
                              sx={{
                                borderBottom: `1px solid ${C.border}`,
                                fontSize: 14,
                                fontWeight: 600,
                                color: '#111827',
                              }}
                            >
                              {row.userTripId}
                            </TableCell>
                            <TableCell
                              sx={{
                                borderBottom: `1px solid ${C.border}`,
                                fontSize: 14,
                                fontWeight: 500,
                                color: '#111827',
                              }}
                            >
                              {row.userOrderId ?? '—'}
                            </TableCell>
                            <TableCell
                              sx={{
                                borderBottom: `1px solid ${C.border}`,
                                fontSize: 13,
                                color: C.greyLabel,
                              }}
                            >
                              {row.firstDriverName ?? '—'}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                borderBottom: `1px solid ${C.border}`,
                                fontSize: 13,
                                color: C.greyLabel,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.relativeTime} ago
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, px: 2 }}>
                  <Button
                    component={RouterLink}
                    to="/trip"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                  >
                    View all trips
                  </Button>
                </Box>
              </Card>
            </Grid2>
      </Grid2>
    </Stack>
  )
}

export default FleetDashboardMetrics
